// src/components/NewsCarousel.js
import React, { useEffect, useRef, useState } from "react";

export default function NewsCarousel({ items = [], intervalMs = 12000 }) {
  const [i, setI] = useState(0);
  const timerRef = useRef(null);
  const wrapRef = useRef(null);

  // --- Tvinga in din pelikan-slide på index 2 (slide 3) ---
  const effectiveItems = (() => {
    const PELICAN_SLIDE = {
      tag: "Miljö",
      title: "Släng inte skräp i skogen",
      text:
        "Hjälp Avfallspelikanen hålla naturen ren – sortera och lämna avfall på rätt plats.",
      img: "/avfallspelikannyhet.png",
      imgAlt: "Avfallspelikanen uppmanar till att inte skräpa ned",
    };

    const arr = [...items];
    if (arr.length < 3) {
      // om original bara hade 1–2 slides, fyll så vi har minst 3
      while (arr.length < 3) arr.push({});
    }
    // ersätt alltid slide 3 med pelikan-bilden
    arr[2] = PELICAN_SLIDE;
    return arr;
  })();

  const len = effectiveItems.length;
  const go = (next) => setI((p) => (p + next + len) % len);
  const to = (idx) => setI(((idx % len) + len) % len);

  const reducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const canAutoplay = !reducedMotion && intervalMs > 0 && len > 1;

  const stop = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };
  const start = () => {
    if (!canAutoplay) return;
    stop();
    timerRef.current = setInterval(() => go(1), intervalMs);
  };

  useEffect(() => {
    start();
    return stop;
  }, [len, intervalMs, reducedMotion]);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "hidden") stop();
      else start();
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [canAutoplay, intervalMs]);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    let startX = 0, dx = 0, active = false, pid = null;
    const getX = (e) => ("touches" in e ? e.touches?.[0]?.clientX : e.clientX);

    const downH = (e) => {
      active = true; dx = 0; pid = e.pointerId ?? null;
      startX = getX(e); stop();
      try { pid != null && el.setPointerCapture && el.setPointerCapture(pid); } catch {}
    };
    const moveH = (e) => { if (!active) return; dx = getX(e) - startX; };
    const upH = () => {
      if (!active) return; active = false;
      if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
      start();
      try { pid != null && el.releasePointerCapture && el.releasePointerCapture(pid); } catch {}
      pid = null;
    };

    el.addEventListener("pointerdown", downH, { passive: true });
    el.addEventListener("pointermove", moveH, { passive: true });
    el.addEventListener("pointerup", upH, { passive: true });
    el.addEventListener("pointercancel", upH, { passive: true });
    return () => {
      el.removeEventListener("pointerdown", downH);
      el.removeEventListener("pointermove", moveH);
      el.removeEventListener("pointerup", upH);
      el.removeEventListener("pointercancel", upH);
    };
  }, [i, intervalMs, canAutoplay]);

  if (!len) return null;
  const cur = effectiveItems[i];

  // === Bildregler ===
  const FALLBACK_IMG = "/nyhetskarusel2.png";
  const isSecondSlide = i === 1;
  const imgSrc = isSecondSlide ? FALLBACK_IMG : cur?.img || null;
  const hasImg = Boolean(imgSrc);
  const cls = `news-slide ${hasImg ? "has-img" : "full"}`;

  // === Hero vs vanlig slide ===
  const isHero = Boolean(cur?.heroTitle);
  const TARGET_H = 340;

  // === Layoutmått ===
  const IMG_COL_BASE = 260;
  const LEFT_MASK_PCT = 0.22;
  const EXTRA_FOR_SECOND = isSecondSlide ? IMG_COL_BASE * LEFT_MASK_PCT : 0;
  const IMG_COL_W = IMG_COL_BASE + EXTRA_FOR_SECOND;

  // === Bildkolumn ===
  const imgBoxStyle = {
    position: "relative",
    width: IMG_COL_W,
    minWidth: IMG_COL_W,
    height: "100%",
    background: "#F6FAF7",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  // === Inre grid ===
  const slideContainerStyle = {
    display: "grid",
    gridTemplateColumns: hasImg ? `${IMG_COL_W}px minmax(0,1fr)` : "1fr",
    alignItems: "stretch",
    width: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
    ...(isHero ? {} : { height: TARGET_H }),
  };

  return (
    <section
      className="news-card"
      aria-roledescription="carousel"
      aria-label="Nyheter"
      onMouseEnter={stop}
      onMouseLeave={start}
      onFocus={stop}
      onBlur={start}
      style={{ position: "relative", width: "100%", overflow: "hidden" }}
    >
      <div
        className={cls}
        key={i}
        role="group"
        aria-roledescription="slide"
        aria-label={`${i + 1} av ${len}`}
        ref={wrapRef}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "ArrowLeft") go(-1);
          if (e.key === "ArrowRight") go(1);
        }}
        style={slideContainerStyle}
      >
        {hasImg && (
          <div className="news-img-box" style={imgBoxStyle}>
            {isSecondSlide ? (
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundImage: `url(${imgSrc})`,
                  backgroundSize: "cover",
                  backgroundPosition: "left center",
                  boxShadow: "inset -22% 0 0 0 #F6FAF7",
                }}
              />
            ) : (
              <img
                src={imgSrc}
                alt={cur?.imgAlt || "Nyhetsbild"}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                  objectPosition: "center",
                  display: "block",
                }}
                loading="lazy"
              />
            )}
          </div>
        )}

        <div
          className="news-body"
          style={{
            padding: "24px 28px",
            minWidth: 0,
            maxWidth: "100%",
            overflow: "hidden",
            background: "transparent",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
          }}
        >
          {cur?.heroTitle ? (
            <>
              <h1 className="dash-title" style={{ margin: 0 }}>
                {cur.heroTitle}
              </h1>
              {cur?.heroSubtitle && (
                <p className="dash-subtitle" style={{ marginTop: 12 }}>
                  {cur.heroSubtitle}
                </p>
              )}
              {cur?.heroActions}
            </>
          ) : (
            <>
              {cur?.tag && <span className="news-tag">{cur.tag}</span>}
              <h3 className="news-title">{cur?.title}</h3>
              {cur?.text && <p className="news-text">{cur.text}</p>}
              {cur?.href && (
                <a className="news-link" href={cur.href}>
                  Läs mer →
                </a>
              )}
            </>
          )}
        </div>

        <button className="news-nav prev" aria-label="Föregående" onClick={() => go(-1)}>‹</button>
        <button className="news-nav next" aria-label="Nästa" onClick={() => go(1)}>›</button>
      </div>

      <div className="news-dots" role="tablist" aria-label="Slides">
        {effectiveItems.map((_, idx) => (
          <button
            key={idx}
            role="tab"
            aria-selected={idx === i}
            className={`dot ${idx === i ? "active" : ""}`}
            onClick={() => to(idx)}
            aria-label={`Gå till slide ${idx + 1}`}
          />
        ))}
      </div>
    </section>
  );
}
